/**
 * RequestDetailView Component
 *
 * Displays full details of a feature request with screenshots, status, and comments.
 *
 * @module components/feedback/RequestDetailView
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useUser } from '../../contexts/AuthContext';
import { FeatureRequestService } from '../../services/featureRequestService';
import CommentThread from './CommentThread';
import {
  FeatureRequest,
  STATUS_BADGES,
  REQUEST_TYPES,
  USER_PRIORITIES,
} from '../../types/featureRequest';
import { formatDistanceToNow } from 'date-fns';

/**
 * RequestDetailView - Display full request details
 */
const RequestDetailView: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();

  // State
  const [request, setRequest] = useState<FeatureRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  /**
   * Fetch request details
   */
  useEffect(() => {
    if (requestId) {
      fetchRequest();
    }
  }, [requestId]);

  const fetchRequest = async () => {
    if (!requestId) return;

    try {
      setLoading(true);
      setError(null);

      const fetchedRequest = await FeatureRequestService.getRequestById(requestId);

      if (!fetchedRequest) {
        setError('Request not found');
        return;
      }

      setRequest(fetchedRequest);
    } catch (err: any) {
      console.error('Error fetching request:', err);
      setError(err.message || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    navigate('/feedback/my-requests');
  };

  /**
   * Format date
   */
  const formatDate = (dateString: string): string => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return dateString;
    }
  };

  /**
   * Get status chip
   */
  const getStatusChip = () => {
    if (!request) return null;

    const config = STATUS_BADGES[request.status];
    return (
      <Chip
        label={
          <Box display="flex" alignItems="center" gap={0.5}>
            <span>{config.icon}</span>
            <span>{config.label}</span>
          </Box>
        }
        sx={{
          bgcolor: config.color,
          color: 'white',
          fontWeight: 'bold',
        }}
      />
    );
  };

  /**
   * Open image in dialog
   */
  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  /**
   * Close image dialog
   */
  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error || !request) {
    return (
      <Box maxWidth={1200} mx="auto" p={3}>
        <Alert severity="error">
          {error || 'Request not found'}
        </Alert>
        <Button onClick={handleBack} startIcon={<BackIcon />} sx={{ mt: 2 }}>
          Back to My Requests
        </Button>
      </Box>
    );
  }

  const typeConfig = REQUEST_TYPES[request.type];
  const priorityConfig = USER_PRIORITIES[request.userPriority];

  return (
    <Box maxWidth={1200} mx="auto" p={3}>
      {/* Back Button */}
      <Button
        startIcon={<BackIcon />}
        onClick={handleBack}
        sx={{ mb: 2 }}
      >
        Back to My Requests
      </Button>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3 }}>
            {/* Status and Type */}
            <Stack direction="row" spacing={1} mb={2}>
              {getStatusChip()}
              <Chip
                label={`${typeConfig.icon} ${typeConfig.label}`}
                variant="outlined"
              />
            </Stack>

            {/* Title */}
            <Typography variant="h4" gutterBottom>
              {request.title}
            </Typography>

            {/* Metadata */}
            <Stack direction="row" spacing={2} mb={3}>
              <Typography variant="body2" color="text.secondary">
                Submitted {formatDate(request.createdAt)}
              </Typography>
              {request.completedAt && (
                <Typography variant="body2" color="text.secondary">
                  • Completed {formatDate(request.completedAt)}
                </Typography>
              )}
            </Stack>

            <Divider sx={{ my: 3 }} />

            {/* Description */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {request.description}
              </Typography>
            </Box>

            {/* Screenshots */}
            {request.screenshots.length > 0 && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Screenshots ({request.screenshots.length})
                </Typography>
                <Grid container spacing={2}>
                  {request.screenshots.map((screenshot) => (
                    <Grid item xs={12} sm={6} md={4} key={screenshot.id}>
                      <Paper
                        elevation={1}
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          '&:hover': {
                            elevation: 4,
                          },
                        }}
                        onClick={() => handleImageClick(screenshot.url)}
                      >
                        <Box
                          component="img"
                          src={screenshot.url}
                          alt={screenshot.fileName}
                          sx={{
                            width: '100%',
                            height: 200,
                            objectFit: 'cover',
                          }}
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Developer Response */}
            {request.publicResponse && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Developer Response
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {request.publicResponse}
                </Typography>
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Comments */}
            <CommentThread requestId={request.id} />
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            {/* Request Info */}
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Request Information
              </Typography>

              <Stack spacing={2}>
                {/* Status */}
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Status
                  </Typography>
                  {getStatusChip()}
                  <Typography variant="caption" display="block" mt={0.5}>
                    {STATUS_BADGES[request.status].description}
                  </Typography>
                </Box>

                {/* Type */}
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Type
                  </Typography>
                  <Chip
                    label={`${typeConfig.icon} ${typeConfig.label}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                {/* Priority */}
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Your Priority
                  </Typography>
                  <Chip
                    label={priorityConfig.label}
                    size="small"
                    sx={{
                      bgcolor: priorityConfig.color,
                      color: 'white',
                    }}
                  />
                </Box>

                {/* Submitted */}
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Submitted
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(request.createdAt)}
                  </Typography>
                </Box>

                {/* Last Updated */}
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(request.updatedAt)}
                  </Typography>
                </Box>

                {/* Completed */}
                {request.completedAt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Completed
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(request.completedAt)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Linked Request (if duplicate) */}
            {request.linkedRequestId && (
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Related Request
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This request is a duplicate of another request.
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate(`/feedback/request/${request.linkedRequestId}`)}
                  sx={{ mt: 1 }}
                >
                  View Original Request
                </Button>
              </Paper>
            )}

            {/* Milestone */}
            {request.assignedToMilestone && (
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Milestone
                </Typography>
                <Chip label={request.assignedToMilestone} color="primary" />
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Image Viewer Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={handleCloseImage}
        maxWidth="lg"
        fullWidth
      >
        <IconButton
          onClick={handleCloseImage}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Screenshot"
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RequestDetailView;
